import React from 'react';
import { Box, Link as MuiLink, Typography } from '@mui/material';

/**
 * Minimal markdown renderer for changelog entry bodies.
 *
 * Supports: ## headings, paragraphs, - bullet lists, **bold**, *italic*, [text](url), `code`.
 * Deliberately small — entries are short, schema is fixed. If we need tables or images,
 * swap in react-markdown.
 */

interface InlineToken {
  type: 'text' | 'bold' | 'italic' | 'code' | 'link';
  content: string;
  href?: string;
}

const INLINE_RE = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;

function tokenizeInline(text: string): InlineToken[] {
  const tokens: InlineToken[] = [];
  let lastIndex = 0;
  for (const m of text.matchAll(INLINE_RE)) {
    if (m.index === undefined) continue;
    if (m.index > lastIndex) {
      tokens.push({ type: 'text', content: text.slice(lastIndex, m.index) });
    }
    const piece = m[0];
    if (piece.startsWith('**')) {
      tokens.push({ type: 'bold', content: piece.slice(2, -2) });
    } else if (piece.startsWith('*')) {
      tokens.push({ type: 'italic', content: piece.slice(1, -1) });
    } else if (piece.startsWith('`')) {
      tokens.push({ type: 'code', content: piece.slice(1, -1) });
    } else if (piece.startsWith('[')) {
      const linkMatch = piece.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (linkMatch) tokens.push({ type: 'link', content: linkMatch[1], href: linkMatch[2] });
    }
    lastIndex = m.index + piece.length;
  }
  if (lastIndex < text.length) {
    tokens.push({ type: 'text', content: text.slice(lastIndex) });
  }
  return tokens;
}

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  return tokenizeInline(text).map((tok, i) => {
    const key = `${keyPrefix}-${i}`;
    switch (tok.type) {
      case 'bold':
        return <strong key={key}>{tok.content}</strong>;
      case 'italic':
        return <em key={key}>{tok.content}</em>;
      case 'code':
        return (
          <Box
            key={key}
            component="code"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.875em',
              bgcolor: 'action.hover',
              px: 0.5,
              borderRadius: 0.5,
            }}
          >
            {tok.content}
          </Box>
        );
      case 'link':
        return (
          <MuiLink key={key} href={safeHref(tok.href)} target="_blank" rel="noreferrer noopener" underline="hover">
            {tok.content}
          </MuiLink>
        );
      default:
        return <React.Fragment key={key}>{tok.content}</React.Fragment>;
    }
  });
}

/**
 * Only http(s) reaches an href. A markdown body is authored in-repo, so this
 * needs commit access to abuse and is defence-in-depth rather than a live
 * hole - but `video` in the frontmatter is already strictly anchored for the
 * same reason, and leaving the link sink ungated next to it is inconsistent.
 * An unsafe href becomes undefined, which renders the text without a link.
 */
function safeHref(href: string | undefined): string | undefined {
  return href && /^https?:\/\//i.test(href) ? href : undefined;
}

export function renderChangelogMarkdown(body: string): React.ReactNode {
  const lines = body.split('\n');
  const blocks: React.ReactNode[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i += 1;
      continue;
    }
    if (line.startsWith('## ')) {
      blocks.push(
        <Typography
          key={`h-${i}`}
          variant="subtitle1"
          fontWeight={600}
          sx={{ mt: 2, mb: 0.5, color: 'text.primary' }}
        >
          {renderInline(line.slice(3).trim(), `h-${i}`)}
        </Typography>
      );
      i += 1;
      continue;
    }
    if (line.startsWith('- ') || line.startsWith('* ')) {
      const items: string[] = [];
      while (i < lines.length && (lines[i].startsWith('- ') || lines[i].startsWith('* '))) {
        items.push(lines[i].slice(2).trim());
        i += 1;
      }
      blocks.push(
        <Box key={`ul-${i}`} component="ul" sx={{ pl: 3, my: 1 }}>
          {items.map((item, idx) => (
            <Box key={`li-${idx}`} component="li" sx={{ mb: 0.5 }}>
              <Typography variant="body2" component="span">
                {renderInline(item, `li-${idx}`)}
              </Typography>
            </Box>
          ))}
        </Box>
      );
      continue;
    }
    // Paragraph — accumulate consecutive non-blank, non-special lines
    const paraLines: string[] = [line];
    i += 1;
    while (i < lines.length && lines[i].trim() && !lines[i].startsWith('## ') && !lines[i].startsWith('- ') && !lines[i].startsWith('* ')) {
      paraLines.push(lines[i]);
      i += 1;
    }
    blocks.push(
      <Typography key={`p-${i}`} variant="body2" sx={{ my: 1, lineHeight: 1.65 }}>
        {renderInline(paraLines.join(' '), `p-${i}`)}
      </Typography>
    );
  }
  return <>{blocks}</>;
}
